import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth-context';
import { api } from '../lib/api';
import {
  LayoutDashboard, KanbanSquare, Users, Mail, MessageSquare,
  CalendarDays, Building2, FileText, Settings, LogOut, Globe2, UserCog, KeyRound,
  UsersRound, Wallet, Menu, X, LifeBuoy, ClipboardList, Trophy,
  Package, ClipboardCheck, Wallet as WalletIcon, Trash2, GraduationCap, Lock,
  FileSignature, RotateCcw, Award,
} from 'lucide-react';
import { Button } from './ui/button';
import { ROLE_LABELS } from '../lib/constants';
import { BRAND } from '../lib/brand';
import LanguageSwitcher from './LanguageSwitcher';
import CrmTenantSwitcher, { useCrmTenant } from './CrmTenantSwitcher';
// ★ 2026-08-29 (Fase 58.M) — Training gamificato agenti
import { TrainingProvider, useTraining } from '../lib/training-context';

// NAV item structure: labels are translated via i18next (`t(item.labelKey)`).
const NAV = [
  { to: '/app', icon: LayoutDashboard, labelKey: 'nav.dashboard', testid: 'nav-dashboard' },
  { to: '/app/training', icon: GraduationCap, labelKey: 'nav.training', testid: 'nav-training' },
  { to: '/app/pipeline', icon: KanbanSquare, labelKey: 'nav.pipeline', testid: 'nav-pipeline' },
  { to: '/app/leads', icon: Users, labelKey: 'nav.leads', testid: 'nav-leads' },
  { to: '/app/email-automations', icon: Mail, labelKey: 'nav.emailSequences', testid: 'nav-email-automations' },
  { to: '/app/whatsapp', icon: MessageSquare, labelKey: 'nav.whatsapp', testid: 'nav-whatsapp' },
  { to: '/app/calendar', icon: CalendarDays, labelKey: 'nav.calendar', testid: 'nav-calendar' },
  { to: '/app/properties', icon: Building2, labelKey: 'nav.property', testid: 'nav-properties' },
  { to: '/app/documents', icon: FileText, labelKey: 'nav.documents', testid: 'nav-documents' },
  // ★ Fase 8 — Contratti: visibile ad amministrazione + Senior (permesso via API)
  { to: '/app/contracts', icon: FileSignature, labelKey: 'nav.contracts', testid: 'nav-contracts', contractsPerm: true },
  { to: '/app/agents', icon: UserCog, labelKey: 'nav.agents', testid: 'nav-agents' },
  { to: '/app/leaderboard', icon: Trophy, labelKey: 'nav.leaderboard', testid: 'nav-leaderboard' },
  { to: '/app/coach', icon: Award, labelKey: 'nav.coach', testid: 'nav-coach' },
  { to: '/app/proposals-library', icon: Package, labelKey: 'nav.proposalsLibrary', testid: 'nav-proposals-library' },
  { to: '/app/proposal-orders', icon: ClipboardCheck, labelKey: 'nav.proposalOrders', testid: 'nav-proposal-orders' },
  { to: '/app/payments', icon: WalletIcon, labelKey: 'nav.payments', testid: 'nav-payments' },
  { to: '/app/incassi', icon: Wallet, labelKey: 'nav.collections', testid: 'nav-incassi', adminOnly: true },
  { to: '/app/transcripts', icon: ClipboardList, labelKey: 'nav.transcripts', testid: 'nav-transcripts', adminOnly: true },
  { to: '/app/affiliates', icon: UsersRound, labelKey: 'nav.affiliates', testid: 'nav-affiliates', adminOnly: true },
  { to: '/app/tickets', icon: LifeBuoy, labelKey: 'nav.tickets', testid: 'nav-tickets' },
  { to: '/app/property-units', icon: Building2, labelKey: 'nav.propertyUnits', testid: 'nav-property-units', adminOnly: true },
  { to: '/app/funnel-dates', icon: CalendarDays, labelKey: 'nav.funnelDates', testid: 'nav-funnel-dates', adminOnly: true },
  { to: '/app/lost-recovery', icon: RotateCcw, labelKey: 'nav.lostRecovery', testid: 'nav-lost-recovery', adminOnly: true },
  { to: '/app/agent-commissions', icon: WalletIcon, labelKey: 'nav.agentCommissions', testid: 'nav-agent-commissions', adminOnly: true },
  { to: '/app/my-commissions', icon: Wallet, labelKey: 'nav.myCommissions', testid: 'nav-my-commissions', hideForAdmin: true },
  { to: '/app/trash', icon: Trash2, labelKey: 'nav.trash', testid: 'nav-trash', adminOnly: true },
  { to: '/app/settings', icon: Settings, labelKey: 'nav.settings', testid: 'nav-settings' },
];

function SidebarContent({ user, visibleNav, loc, onLogout, nav, onClose, badgeCounts }) {
  const { t } = useTranslation();
  // ★ Fase 58.M — stato training per badge sulla voce di menu
  const { state: training } = useTraining();
  return (
    <>
      <div className="px-4 md:px-5 py-4 md:py-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <img src={BRAND.greenParadiseLogoSmall} alt={t('common.appName')} className="w-9 h-9 rounded-lg object-contain bg-[var(--surface-2)] p-1 shrink-0" />
          <div className="min-w-0">
            <div className="font-serif text-base md:text-lg leading-tight text-[hsl(var(--primary))] truncate">{t('common.appName')}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground truncate">{t('common.appSubtitle')}</div>
          </div>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="md:hidden shrink-0"
            data-testid="mobile-sidebar-close"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {visibleNav.map((item) => {
          const active =
            item.to === '/app'
              ? loc.pathname === '/app'
              : loc.pathname.startsWith(item.to);
          const Icon = item.icon;
          // ★ 2026-08-11 (Fase 36) — Badge dinamico per Ordini Proposte
          const badge = badgeCounts && badgeCounts[item.testid];
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 mx-1 my-0.5 rounded-md text-sm transition-colors ${
                active
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                  : 'text-foreground hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate flex-1">{t(item.labelKey)}</span>
              {/* ★ Fase 58.M — chip progresso Training (lock finché obbligatorio) */}
              {item.testid === 'nav-training' && training && !training.completed_at && (
                <span
                  className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10px] font-semibold shrink-0 ${
                    active
                      ? 'bg-white/25 text-white'
                      : training.locked
                        ? 'bg-[#F3E7C7] text-[#6A4B00]'
                        : 'bg-[#D6F0F0] text-[#0E5A5A]'
                  }`}
                  data-testid="nav-training-progress-chip"
                >
                  {training.locked && <Lock className="w-2.5 h-2.5" />}
                  {training.progress?.percent || 0}%
                </span>
              )}
              {badge > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold shrink-0 ${
                    active
                      ? 'bg-white/25 text-white'
                      : 'bg-red-500 text-white'
                  }`}
                  data-testid={`${item.testid}-badge`}
                  aria-label={`${badge} ${t('common.pendingCount', 'in attesa')}`}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="px-2 py-2 rounded-md bg-[var(--surface-2)]">
          <div className="text-xs text-muted-foreground">{t('nav.connectedAs')}</div>
          <div className="text-sm font-medium leading-tight truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start mt-2"
          onClick={() => { onClose && onClose(); nav('/change-password'); }}
          data-testid="change-password-button"
        >
          <KeyRound className="w-4 h-4 mr-2" /> {t('nav.changePassword')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start mt-1"
          onClick={() => { onClose && onClose(); onLogout(); nav('/login'); }}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
        </Button>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground px-3 py-2 mt-1"
          data-testid="open-public-site"
        >
          <Globe2 className="w-3 h-3" /> {t('nav.openPublicSite')}
        </a>
      </div>
    </>
  );
}

export function AppShell({ children }) {
  // ★ 2026-08-29 (Fase 58.M) — Il provider Training avvolge tutta la shell:
  // traccia le route visitate (missioni "visita") e alimenta banner + badge.
  return (
    <TrainingProvider>
      <AppShellInner>{children}</AppShellInner>
    </TrainingProvider>
  );
}

function AppShellInner({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // ★ Fase 58.M — training obbligatorio: banner + redirect iniziale
  const { state: training } = useTraining();
  // ★ 2026-08-11 (Fase 36) — Badge dinamico "Ordini proposte" con conteggio pending
  const [badgeCounts, setBadgeCounts] = useState({});
  // ★ 2026-08-28 (Phase 57.CRM-LANG.4) — Tenant CRM (IT/ES/EN)
  // Quando cambia, il key del <main> cambia → re-mount della pagina → refetch automatico
  const { tenant } = useCrmTenant();
  // ★ Fase 8 — visibilità voce "Contratti" (admin sempre, commerciali solo se Senior)
  const [contractsAllowed, setContractsAllowed] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (['admin', 'amministrativo'].includes(user.role)) {
      setContractsAllowed(true);
      return;
    }
    let cancelled = false;
    api.get('/contracts/permissions')
      .then(({ data }) => { if (!cancelled) setContractsAllowed(!!data?.can_send); })
      .catch(() => { /* noop — voce nascosta di default */ });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get('/proposal-orders/pending-count');
        if (!cancelled) {
          setBadgeCounts((prev) => ({ ...prev, 'nav-proposal-orders': data?.count || 0 }));
        }
      } catch { /* noop — badge non è critico */ }
      // ★ 2026-08-11 (Fase 37) — Badge lead nel cestino (solo admin/amministrativo)
      if (['admin', 'amministrativo'].includes(user?.role)) {
        try {
          const { data } = await api.get('/leads/trash/count');
          if (!cancelled) {
            setBadgeCounts((prev) => ({ ...prev, 'nav-trash': data?.count || 0 }));
          }
        } catch { /* noop */ }
      }
    };
    fetchBadges();
    // Refresh ogni 60 secondi + quando la location cambia + quando cambia tenant
    const t = setInterval(fetchBadges, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, loc.pathname, tenant]);

  const visibleNav = useMemo(
    () => NAV.filter((item) => {
      const isAdmin = ['admin', 'amministrativo'].includes(user?.role);
      if (item.adminOnly && !isAdmin) return false;
      if (item.hideForAdmin && isAdmin) return false;
      if (item.contractsPerm && !contractsAllowed) return false;
      return true;
    }),
    [user?.role, contractsAllowed],
  );

  const currentItem = NAV.find((n) =>
    n.to === '/app' ? loc.pathname === '/app' : loc.pathname.startsWith(n.to)
  );
  const currentLabel = currentItem ? t(currentItem.labelKey) : '';

  // ★ Fase 58.M — al primo accesso della sessione, se il training è
  // obbligatorio e non completato, porta l'agente direttamente al Training.
  useEffect(() => {
    if (!training?.locked) return;
    if (sessionStorage.getItem('gp_training_redirected')) return;
    sessionStorage.setItem('gp_training_redirected', '1');
    if (!loc.pathname.startsWith('/app/training')) {
      nav('/app/training');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [training?.locked]);

  return (
    <div className="flex h-screen w-full bg-[var(--surface-2)]">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 border-r border-border bg-[var(--surface-1)] shrink-0"
        data-testid="app-sidebar"
      >
        <SidebarContent
          user={user}
          visibleNav={visibleNav}
          loc={loc}
          onLogout={logout}
          nav={nav}
          badgeCounts={badgeCounts}
        />
      </aside>

      {/* Mobile sidebar overlay + drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-sidebar-overlay"
          />
          <aside
            className="fixed inset-y-0 left-0 w-72 bg-[var(--surface-1)] border-r border-border z-50 flex flex-col md:hidden animate-in slide-in-from-left duration-200"
            data-testid="app-sidebar-mobile"
          >
            <SidebarContent
              user={user}
              visibleNav={visibleNav}
              loc={loc}
              onLogout={logout}
              nav={nav}
              onClose={() => setMobileOpen(false)}
              badgeCounts={badgeCounts}
            />
          </aside>
        </>
      )}

      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-[var(--surface-1)] flex items-center justify-between px-3 md:px-6 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(true)}
              className="md:hidden"
              data-testid="mobile-sidebar-open"
              aria-label={t('common.open')}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="font-serif text-base md:text-lg text-[hsl(var(--primary))] md:hidden truncate">{t('common.appName')}</div>
            <div className="hidden md:block text-sm text-muted-foreground truncate">{currentLabel}</div>
          </div>
          <div className="flex items-center gap-2">
            <CrmTenantSwitcher />
            {/* ★ Fase 32 — utenti con tenant bloccato (rete ES): lingua fissa, niente switcher */}
            {!user?.crm_tenant_locked && <LanguageSwitcher variant="compact" />}
            <span className="hidden md:inline text-xs text-muted-foreground font-mono">{t('common.version')}</span>
          </div>
        </header>

        {/* ★ Fase 58.M — banner training obbligatorio (persistente fuori dal Training) */}
        {training?.locked && !loc.pathname.startsWith('/app/training') && (
          <div
            className="flex items-center gap-3 px-3 md:px-6 py-2 bg-[#FBF3DF] border-b border-[#E4C88A] text-[#6A4B00] shrink-0"
            data-testid="training-mandatory-banner"
          >
            <Lock className="w-4 h-4 shrink-0" />
            <span className="text-xs md:text-sm flex-1 min-w-0 truncate">
              <span className="font-semibold">{t('training.mandatoryBanner.title', 'Training obbligatorio:')}</span> {t('training.mandatoryBanner.body', 'completa le missioni per certificarti —')} {training.progress?.percent || 0}% {t('training.mandatoryBanner.done', 'fatto')}
            </span>
            <Button
              size="sm"
              variant="secondary"
              className="h-7 shrink-0 border border-[#E4C88A] bg-[#F3E7C7] text-[#6A4B00] hover:bg-[#EEDDB4]"
              onClick={() => nav('/app/training')}
              data-testid="training-banner-cta"
            >
              <GraduationCap className="w-3.5 h-3.5 mr-1" /> {t('training.mandatoryBanner.cta', 'Vai al Training')}
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-auto" key={`tenant-${tenant}`} data-crm-tenant={tenant}>
          {children}
        </div>
      </main>
    </div>
  );
}
